import type { InAppAnnouncementCard } from "schemas/inAppAnnouncement";

import * as React from "react";

import { AnnouncementCarousel } from "@replohq/lattice/blocks/announcement-carousel/AnnouncementCarousel";
import { Button } from "@replohq/lattice/components/button/Button";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useOpenInAppAnnouncementDestination } from "@v2/features/in-app-announcement/useOpenInAppAnnouncementDestination";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { inAppAnnouncementClient } from "@v2/trpc/routes/inAppAnnouncement";
import { Sparkles } from "lucide-react";
import { useInAppAnnouncementSession } from "replo-utils/react/use-in-app-announcement-session";

type AnnouncementSurface = "agent" | "theme-builder";
const AnnouncementContext = React.createContext<{
  announcements: InAppAnnouncementCard[];
  userId: string;
  surface: AnnouncementSurface;
  replayNonce: number;
  replayRequested: boolean;
  closeReplay: () => void;
  replay: () => void;
} | null>(null);

export function InAppAnnouncementProvider({
  surface,
  children,
}: React.PropsWithChildren<{ surface: AnnouncementSurface }>) {
  const { user } = useCurrentUser();
  if (!user) {
    return children;
  }
  return (
    <UserInAppAnnouncements key={user.id} userId={user.id} surface={surface}>
      {children}
    </UserInAppAnnouncements>
  );
}

function UserInAppAnnouncements({
  userId,
  surface,
  children,
}: React.PropsWithChildren<{ userId: string; surface: AnnouncementSurface }>) {
  const [replay, setReplay] = React.useState({ nonce: 0, requested: false });
  const { data } = useQuery({
    queryKey: ["in-app-announcement", userId, surface],
    queryFn: () => inAppAnnouncementClient.list.query({ surface }),
    staleTime: 60_000,
    retry: 1,
    meta: { reploIgnoreDefaultErrorHandling: true },
  });
  return (
    <AnnouncementContext.Provider
      value={{
        announcements: data?.announcements ?? [],
        userId,
        surface,
        replayNonce: replay.nonce,
        replayRequested: replay.requested,
        closeReplay: () => {
          setReplay((current) => ({ ...current, requested: false }));
        },
        replay: () => {
          setReplay((current) => ({
            nonce: current.nonce + 1,
            requested: true,
          }));
        },
      }}
    >
      {children}
    </AnnouncementContext.Provider>
  );
}

export function InAppAnnouncementReplayButton() {
  const context = React.useContext(AnnouncementContext);
  if (!context?.announcements.length) {
    return null;
  }
  return (
    <Button
      variant="tertiary"
      className="min-h-11"
      startEnhancer={<Sparkles className="size-4" />}
      onClick={context.replay}
    >
      What's new
    </Button>
  );
}

export function InAppAnnouncements() {
  const context = React.useContext(AnnouncementContext);
  if (!context) {
    return null;
  }
  return <AnnouncementSession key={context.replayNonce} {...context} />;
}

function AnnouncementSession({
  announcements,
  userId,
  surface,
  replayRequested,
  closeReplay,
}: {
  announcements: InAppAnnouncementCard[];
  userId: string;
  surface: AnnouncementSurface;
  replayRequested: boolean;
  closeReplay: () => void;
}) {
  const session = useInAppAnnouncementSession({
    announcements,
    userId,
    replay: replayRequested,
  });
  const { queue, open } = session;
  const openDestination = useOpenInAppAnnouncementDestination();
  const { mutate: track } = useMutation({
    mutationFn: ({
      announcement,
      event,
    }: {
      announcement: InAppAnnouncementCard;
      event: "viewed" | "engaged" | "dismissed";
    }) => {
      return inAppAnnouncementClient.record.mutate({
        announcementId: announcement.id,
        resetKey: announcement.resetKey,
        event,
        surface,
      });
    },
    retry: 1,
    meta: { reploIgnoreDefaultErrorHandling: true },
  });
  const onViewed = React.useCallback(
    (announcement: InAppAnnouncementCard) => {
      track({ announcement, event: "viewed" });
    },
    [track],
  );
  // Every close path settles all viewed slides; only the engaged one skips "dismissed".
  const close = (
    viewed: InAppAnnouncementCard[],
    engaged?: InAppAnnouncementCard,
  ) => {
    closeReplay();
    for (const announcement of viewed) {
      track({
        announcement,
        event: announcement.id === engaged?.id ? "engaged" : "dismissed",
      });
    }
    session.dismiss(viewed);
  };
  return (
    <AnnouncementCarousel
      key={queue
        .map((announcement) => `${announcement.id}:${announcement.resetKey}`)
        .join(",")}
      open={open}
      slides={queue.map((announcement) => ({
        ...announcement,
        title: announcement.name,
        description: announcement.blurb,
        actionLabel: announcement.ctaLabel,
      }))}
      onViewed={onViewed}
      onDismiss={close}
      onEngage={(announcement, viewed) => {
        close(viewed, announcement);
        openDestination(announcement.destination);
      }}
    />
  );
}
